import h from 'stringjsx';
import I18n from '@/modules/i18n';
import {axios} from '@/modules/request/axios_configuration';
import {onDocumentReady} from '@/functions/dom';
import Comment from './comment';
import {mount as mountSaleDocumentDiscrepancy} from './sale_document_discrepancy';

const COMMENT_THREAD_DIALOG_WIDTH = 755;
const COMMENT_THREAD_DIALOG_HEIGHT = 800;

function shouldRenderUnsavedSaleDocumentDiscrepancy(parentType) {
  return (
    parentType === `InvoiceTranscription` &&
    /^\/invoice_transcriptions\/\d+\/edit$/.test(window.location.pathname)
  );
}

function commentThreadDialogKey(parentType, parentId) {
  return `${parentType}:${parentId}`;
}

function isCommentThreadsIndexPage() {
  return /^\/comment_threads(?:\/)?(?:\?|$)/.test(window.location.pathname);
}

function focusCommentThreadDialog(dialog) {
  if (!dialog) return;

  dialog.elements.dialog.focus();
  dialog.elements.body.querySelector(`.js-comment-thread-text`)?.focus();
}

function closeCommentThreadDialogs({exceptKey} = {}) {
  Object.entries(window.commentThreadDialogs || {}).forEach(([key, dialog]) => {
    if (key === exceptKey || !dialog?.isOpen?.()) return;
    dialog.close();
  });
}

function moveCommentThreadDialogToRight(dialog) {
  if (!isCommentThreadsIndexPage()) return;

  window.requestAnimationFrame(() => dialog.moveTo(1000, 100));
}

export class CommentThread {
  constructor(parent_id, parent_type) {
    this.parent_id = parent_id;
    this.parent_type = parent_type;
    this.id = null;
    this.saleDocumentDiscrepancyId = null;
    this.open = false;
    this.comments = [];
    this.canOpenCloseThread = false;
    this.target = null;
  }

  init() {
    return axios({
      method: `GET`,
      url: `/comment_threads/${this.parent_type}/${this.parent_id}`,
      success: ({data}) => {
        this.id = data.id;
        this.saleDocumentDiscrepancyId = data.sale_document_discrepancy_id;
        this.open = data.open;
        this.canOpenCloseThread = data.can_open_close_thread;
        this.target = data.target;
        this.comments = data.comments.map(comment => new Comment(comment));
      },
      error: err => {
        alertify.closeAll();
        alertify.error(I18n.t(`error`));
      }
    });
  }

  render() {
    return (
      <div class="polymorphic_comments" data-parent-type={this.parent_type} data-parent-id={this.parent_id}>
        <div js-sale-document-discrepancy></div>
        <div js-comment-thread-target-holder>
          {this.renderTargetSelect()}
        </div>
        <div class="input_comments m-b-2">
          <p>{I18n.t(`comment_threads.new_comment`)}:</p>
          <form>
            <textarea class="js-comment-thread-text" cols="65" rows="2"></textarea>
            <br />
            <button class="js-save-comment buttons-standard">{I18n.t(`save`)}</button>
          </form>
        </div>
        <h3 class="js-open">{this.renderOpen()}</h3>
        <div class="js-toggle-open-thread-holder">
          {this.renderButtonOpen()}
        </div>
        <div class="js-comments-holder">
          {this.comments.map(comment => comment.render())}
        </div>
      </div>
    );
  }

  renderOpen(rerender = false) {
    const html = I18n.t(`comment_threads.comment_thread_is`, {open: (this.open ? I18n.t(`open`) : I18n.t`closed`)});
    if (rerender) {
      this.html_data.find(`.js-open`).html(html);
    }

    return html;
  }

  renderButtonOpen(rerender = false) {
    if (!this.canOpenCloseThread) {
      if (rerender) {
        this.html_data.find(`.js-toggle-open-thread-holder`).html(``);
      }

      return ``;
    }
    const button_text = this.open ? I18n.t(`close`) : I18n.t(`open`);

    const classes = `js-toggle-open-thread buttons-standard ${this.comments.length === 0 ? `invisible` : ``}`;
    const html = <button class={classes}>{button_text}</button>;
    if (rerender) this.html_data.find(`.js-toggle-open-thread-holder`).html(html.toString());

    return html;
  }

  renderTargetSelect(rerender = false) {
    const html = this.comments.length === 0 ? `` : (
      <div class="m-b-2">
        <label>
          {I18n.t(`comment_threads.target`)}:
          <select
            class="js-comment-thread-target m-l-1"
          >
            <option value="customer" selected={this.target === `customer`}>
              {I18n.t(`comment_threads.targets.customer`)}
            </option>
            <option value="accountant" selected={this.target === `accountant`}>
              {I18n.t(`comment_threads.targets.accountant`)}
            </option>
          </select>
        </label>
      </div>
    );

    if (rerender) {
      this.html_data.find(`[js-comment-thread-target-holder]`).html(html.toString());
    }

    return html;
  }

  ready() {
    this.html_data = $(`.polymorphic_comments[data-parent-type="${this.parent_type}"][data-parent-id="${this.parent_id}"]`).last();
    this.html_data.find(`.js-save-comment`).click(this.save.bind(this));
    this.html_data.on(`click`, `.js-toggle-open-thread`, this.toggleOpen.bind(this));
    this.html_data.on(`change`, `.js-comment-thread-target`, this.updateTarget.bind(this));
    this.html_data.find(`.js-comment-thread-text`).focus();

    if (
      this.parent_type == `Sale` ||
      this.parent_type == `Purchase` ||
      this.parent_type == `InvoiceTranscription`
    ) {
      if (
        this.saleDocumentDiscrepancyId ||
        shouldRenderUnsavedSaleDocumentDiscrepancy(this.parent_type)
      ) {
        mountSaleDocumentDiscrepancy(
          this.html_data.find(`[js-sale-document-discrepancy]`)[0],
          this.parent_type,
          this.parent_id,
          this.id,
        );
      }
    }
  }

  updateTarget(event) {
    const target = event.currentTarget.value;

    if (!this.id) {
      this.target = target;
      return;
    }

    axios({
      method: `POST`,
      url: `/comment_threads/update_target`,
      data: {
        comment_thread: {
          parent_id: this.parent_id,
          parent_type: this.parent_type,
          target,
        }
      },
      success: ({data}) => {
        this.id = data.id;
        this.target = data.target;
        this.renderTargetSelect(true);
      },
    });
  }

  toggleOpen() {
    axios({
      method: `POST`,
      url: `/comment_threads/toggle_open`,
      data: {
        comment_thread: {
          parent_id: this.parent_id,
          parent_type: this.parent_type
        }
      },
      success: ({data}) => {
        this.open = data.open;
        this.renderOpen(true);
        this.renderButtonOpen(true);
      },
    });

    return false;
  }

  save() {
    const text = this.html_data.find(`.js-comment-thread-text`).val();
    if (!text.length) return false;

    axios({
      method: `POST`,
      url: `/comment_threads`,
      data: {
        comment_thread: {
          parent_id: this.parent_id,
          parent_type: this.parent_type,
          comment: {text},
        }
      },
      success: ({data}) => {
        this.id = data.id;
        this.open = true;
        this.target = data.target;
        this.canOpenCloseThread = data.can_open_close_thread;
        const comment = new Comment(data.comment);
        this.comments.unshift(comment);
        this.renderOpen(true);
        this.renderButtonOpen(true);
        this.renderTargetSelect(true);
        this.html_data.find(`.js-comments-holder`).prepend(comment.render());
        this.html_data.find(`.js-comment-thread-text`).val(``);
        $(`.js-comment_thread[data-parent-type="${this.parent_type}"][data-parent-id="${this.parent_id}"]`)
          .text(this.comments.length)
          .attr(`data-comment-count`, this.comments.length);

        if (window.location.pathname.endsWith(`comment_threads`)) {
          const tableRow = document.querySelector(`tr[js-comment-thread-parent-type="${this.parent_type}"][js-comment-thread-parent-id="${this.parent_id}"]`);
          const lastCommentedTd = tableRow.querySelector(`td[js-last-commenter]`);

          function truncateText(text) {
            if (text.length > 40) {
              return text.substring(0, 40) + `...`;
            }
            return text;
          }

          lastCommentedTd.querySelector(`[js-last-commented_by]`).innerHTML = comment.updated_by.filtered_name;
          lastCommentedTd.querySelector(`[js-last-commented-at]`).innerHTML = comment.updated_at.split(`T`)[0];
          lastCommentedTd.querySelector(`[js-last-comment]`).innerHTML = truncateText(comment.text);
          lastCommentedTd.setAttribute(`abbr`, comment.updated_by.filtered_name);
        }
      },
    });
    return false;
  }
}

export function commentThreadLink(parentType, parentId, commentCount = 0, className = ``) {
  const onclickHandle = `window.openCommentThread(${parentId},'${parentType}'); return false;`;
  return `
    <a
      href="#"
      class="js-comment_thread ${className}"
      data-parent-type=${parentType}
      data-parent-id=${parentId}
      data-comment-count=${commentCount}
      onclick="${onclickHandle}">
      ${commentCount}
    </a>
  `;
}

export function initializeGlobalCommentThreads() {
  window.commentThreadDialogs ||= {};
  window.closeCommentThreadDialogs = closeCommentThreadDialogs;
  window.openCommentThread = function(parentId, parentType) {
    const key = commentThreadDialogKey(parentType, parentId);
    const existingDialog = window.commentThreadDialogs[key];

    if (existingDialog?.isOpen?.()) {
      focusCommentThreadDialog(existingDialog);
      return existingDialog;
    }

    if (isCommentThreadsIndexPage()) {
      closeCommentThreadDialogs();
    }

    return alertify.CommentThread(parentId, parentType);
  };
  window.commentThreadButton = commentThreadLink;
  window.commentThreadLink = commentThreadLink;

  onDocumentReady(() => {
    if (!alertify.CommentThread) {
      alertify.dialog(`CommentThread`, function factory() {
        return {
          main: function(parent_id, parent_type) {
            this.setting(`parent_id`, parent_id);
            this.setting(`parent_type`, parent_type);
            this.setting(`dialog_key`, commentThreadDialogKey(parent_type, parent_id));
            this.message = I18n.t(`loading`);

            window.commentThreadDialogs[this.settings.dialog_key] = this;
          },
          setup: function() {
            return {
              buttons: [
                {
                  text: I18n.t(`close_all_comment`),
                  attrs: {
                    onClick: `window.closeCommentThreadDialogs();`
                  }
                },
                {
                  text: I18n.t(`cancel`),
                  key: 27 /*Esc*/
                },
              ],
              options: {
                title: I18n.t(`loading`),
                movable: true,
                resizable: true,
                closable: true,
                maximizable: true,
                modal: false,
              }
            };
          },
          settings: {
            parent_id: null,
            parent_type: null,
            dialog_key: null,
          },
          prepare: function() {
            const comments = new CommentThread(
              this.settings.parent_id,
              this.settings.parent_type,
            );
            comments.init().then(() => {
              this.setContent(comments.render().toString());
              comments.ready();
            });
            this.setHeader(
              `${I18n.t(`comment_threads.comments_on`)} ` +
              `${I18n.t(`comment_threads.parent_types.${comments.parent_type}`)}` +
              `(${comments.parent_id})`
            );
            this.resizeTo(COMMENT_THREAD_DIALOG_WIDTH, COMMENT_THREAD_DIALOG_HEIGHT);
            moveCommentThreadDialogToRight(this);
          },
          hooks: {
            onclose: function() {
              delete window.commentThreadDialogs[this.settings.dialog_key];
            },
          },
        };
      }, true);
    }
  });
}
